import { useCallback, useEffect, useState } from "react";
import { Segmented, Space, Tag, Tooltip, message } from "antd";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { ApiService } from "../../api";
import type { AdminGroup, IntegrationStatus, OrgUnit } from "../../api";
import { apiErrorMessage } from "../../api/apiError";
import { PageHeader } from "../../components/PageHeader";
import { MAX_PAGE_WIDTH } from "../Reports/ui";
import { space } from "../../theme/space";
import DirectoryPanel from "./DirectoryPanel";
import GroupsPanel from "./GroupsPanel";
import LocationsPanel from "./LocationsPanel";
import PeoplePanel from "./PeoplePanel";
import StructurePanel from "./StructurePanel";

type TabKey = "people" | "groups" | "structure" | "places" | "directory";

/**
 * One place to answer "who works here, and who do they answer to?".
 *
 * All four tabs were Django admin until now, which is a developer surface: it
 * exposes every model in the project and has no idea which templates somebody
 * administers, so the only way to let a customer add a starter was to hand out
 * a superuser login.
 *
 * **One page and not five.** They are one job. Adding somebody means putting
 * them in a unit, naming their manager and dropping them into a group, and a
 * screen that made those three navigations would be describing the data model
 * rather than the task. Places sit here for the same reason: a site has a
 * responsible person and a unit that runs it, so maintaining one is the same
 * visit as maintaining the org chart, not a different screen.
 *
 * The meta line carries the number that decides whether any of it *works*: an
 * approval slot set to "the requester's manager signs" resolves to nobody for
 * every person with no manager recorded, and a case that reaches such a slot
 * stops on a step with no button on it. Better to see the count than to find
 * out one permit at a time.
 */
const OrganisationPage = () => {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const tab = (params.get("tab") as TabKey) || "people";
  const [units, setUnits] = useState<OrgUnit[]>([]);
  const [groups, setGroups] = useState<AdminGroup[]>([]);
  const [status, setStatus] = useState<IntegrationStatus | null>(null);

  // The three things every tab needs, fetched once here rather than per panel:
  // the unit list, the group list and the integration status are read by two
  // or more of the four, and four copies of each fetch is four chances for
  // two tabs to disagree about what exists.
  const loadShared = useCallback(async () => {
    try {
      const [unitRows, groupRows, statusRow] = await Promise.all([
        ApiService.apiAccountsOrgUnitsList(),
        ApiService.apiAccountsAdminGroupsList(),
        ApiService.apiAccountsIntegrationStatus(),
      ]);
      setUnits(unitRows);
      setGroups(groupRows);
      setStatus(statusRow);
    } catch (e) {
      message.error(apiErrorMessage(e, t("Could not load the organisation")));
    }
  }, [t]);

  useEffect(() => {
    loadShared();
  }, [loadShared]);

  const setTab = (next: TabKey) => {
    // In the URL so a tab is linkable and survives a reload -- these screens
    // are revisited, not walked through once.
    params.set("tab", next);
    setParams(params, { replace: true });
  };

  const withoutManager = status?.users_without_manager ?? 0;

  return (
    <div style={{ padding: space.wide, maxWidth: MAX_PAGE_WIDTH }}>
      <PageHeader
        title={t("Organisation")}
        meta={
          <Space size={space.tight} wrap>
            <span>
              {t("{{count}} unit(s)", { count: status?.org_units ?? 0 })}
            </span>
            {withoutManager > 0 && (
              <Tooltip
                title={t(
                  "An approval slot set to 'the requester's manager signs' cannot be filled for them. Give them a manager on the People tab.",
                )}
              >
                <Tag color="warning">
                  {withoutManager === 1
                    ? t("1 person has no manager")
                    : t("{{count}} people have no manager", {
                        count: withoutManager,
                      })}
                </Tag>
              </Tooltip>
            )}
            {status?.directory.enabled && (
              <Tag color="blue">{t("Connected to a directory")}</Tag>
            )}
          </Space>
        }
      />

      <Segmented
        value={tab}
        onChange={(v) => setTab(v as TabKey)}
        style={{ marginBottom: space.base }}
        options={[
          { value: "people", label: t("People") },
          { value: "groups", label: t("Groups") },
          { value: "structure", label: t("Structure") },
          { value: "places", label: t("Places") },
          { value: "directory", label: t("Directory") },
        ]}
      />

      {tab === "people" && (
        <PeoplePanel units={units} groups={groups} onChanged={loadShared} />
      )}
      {tab === "groups" && <GroupsPanel onChanged={loadShared} />}
      {tab === "structure" && (
        <StructurePanel units={units} onChanged={loadShared} />
      )}
      {tab === "places" && (
        <LocationsPanel units={units} onChanged={loadShared} />
      )}
      {tab === "directory" && (
        <DirectoryPanel status={status} groups={groups} onChanged={loadShared} />
      )}
    </div>
  );
};

export default OrganisationPage;
